import { useCallback } from "react";
import {
  getRecipientsWithCc,
  getSubject,
  getBody,
  getAttachmentItems,
  getAttachmentContent,
  removeAttachment,
  addBase64Attachment,
  setSubject,
  setBody,
  getUserEmail,
  getEmlBase64,
  saveCompose,
  // captureEmailSnapshot,       // TODO: re-enable with Sent Items copy
  // saveUnencryptedCopyToSentItems, // TODO: re-enable with Sent Items copy
} from "../utils/officeItemHelpers";
import {
  isBlockedAttachment,
  getFileExtension,
} from "../utils/blockedextensions-helper";
import { useI18n } from "../contexts/I18nContext";

/**
 * Fetches the Avamboo logo and returns it as a base64 data URL.
 * Soft-fail: returns null if the fetch fails (e.g., asset not available).
 * @returns {Promise<string|null>}
 */
async function _fetchLogoBase64() {
  try {
    const resp = await fetch("/assets/secured-by-ae.png");
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * useSendOrchestrator
 *
 * Implements the real encryption workflow called by PasswordModal action buttons.
 *
 * @param {object} params
 * @param {object} params.pdfEngine       - PdfEngineClient instance
 * @param {object|null} params.settings   - BenutzerSettings
 * @param {boolean} params.isPaid         - License type
 * @returns {{ handleEncryptAndSend, handleSendUnencrypted }}
 */
export function useSendOrchestrator({ pdfEngine, settings, isPaid }) {
  const { t } = useI18n();
  // ─── Post-send helper ─────────────────────────────────────────────────────

  /**
   * Runs after saveCompose() for encrypted send modes.
   * Optionally saves an unencrypted copy to Sent Items (soft-fail).
   *
   * @param {"full"|"pdfOnly"} sendMode
   * @param {object} originalSnapshot - Result of captureEmailSnapshot()
   * @returns {Promise<{ sendMode: string, sentItemsFailed: boolean }>}
   */
  // TODO: Re-enable once Sent Items copy is properly implemented
  //       (save during prepare, delete on failure if send never happens).
  // const _runPostSend = useCallback(
  //   async (sendMode, originalSnapshot) => {
  //     let sentItemsFailed = false;
  //     if (settings?.entschluesseltGesendetSpeichern) {
  //       try {
  //         await saveUnencryptedCopyToSentItems(originalSnapshot);
  //       } catch (err) {
  //         sentItemsFailed = true;
  //         try {
  //           localStorage.setItem("avamboo_pending_warning", JSON.stringify({
  //             type: "warning",
  //             message: "Die E-Mail konnte nicht unverschlüsselt unter 'Gesendete Elemente' gespeichert werden.",
  //             subject: originalSnapshot?.subject ?? "",
  //           }));
  //         } catch (_) {}
  //       }
  //     }
  //     return { sendMode, sentItemsFailed };
  //   },
  //   [settings],
  // );

  // ─── Mode A: Encrypt only PDF attachments ──────────────────────────────────

  const _encryptPdfAttachmentsOnly = useCallback(
    async (password) => {
      // 0. Snapshot disabled — Sent Items copy not yet implemented.
      // const originalSnapshot = await captureEmailSnapshot();

      // 1. Collect non-inline PDF attachments
      const allItems = await getAttachmentItems();
      const pdfItems = allItems.filter(
        (a) => !a.isInline && a.name?.toLowerCase().endsWith(".pdf"),
      );

      const skipped = []; // PDF_ALREADY_ENCRYPTED filenames
      const encrypted = []; // { id, name, base64 }

      // 2. Encrypt each PDF
      for (const item of pdfItems) {
        try {
          const { content } = await getAttachmentContent(item.id);
          const result = await pdfEngine.encryptPdf(content, password);
          encrypted.push({ id: item.id, name: item.name, base64: result.data });
        } catch (err) {
          if (err.code === "PDF_ALREADY_ENCRYPTED") {
            skipped.push(item.name);
            continue;
          }
          // Fatal: show error and re-throw so PasswordModal shows "unverschlüsselt senden?" confirm
          window.alert(
            t("Fehler beim Verschlüsseln\n\nDie PDF-Datei \"{name}\" konnte nicht verschlüsselt werden.", { name: item.name }),
          );
          throw err;
        }
      }

      // 3. Informational message for skipped (already encrypted) PDFs
      if (skipped.length > 0) {
        const msg =
          skipped.length === 1
            ? t("Ein PDF-Anhang dieser E-Mail ist bereits passwortgeschützt und wird nicht erneut verschlüsselt.")
            : t("{count} PDF-Anhänge dieser E-Mail sind bereits passwortgeschützt und werden nicht erneut verschlüsselt.", { count: skipped.length });
        window.alert(msg);
      }

      // 4. Replace originals with encrypted versions
      for (const enc of encrypted) {
        await removeAttachment(enc.id);
        await addBase64Attachment(enc.base64, enc.name, false);
      }

      // 5. Save changes to compose item (attachments are now encrypted).
      //    The compose window stays open — user sends manually via Outlook's Send button.
      await saveCompose();

      // 6. TODO: Sent Items copy disabled — will be re-enabled once properly implemented.
      // return await _runPostSend("pdfOnly", originalSnapshot);
      return { sendMode: "pdfOnly", sentItemsFailed: false };
    },
    [pdfEngine, t], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ─── Mode B: Build full container PDF ─────────────────────────────────────

  const _buildAndSendContainer = useCallback(
    async (password, keepSubjectUnencrypted) => {
      const defaultBodyHtml =
        `<p>${t("Diese E-Mail wurde mit <strong>Avamboo Encrypt</strong> verschlüsselt.")}</p>` +
        `<p>${t("Bitte öffnen Sie den Anhang mit dem Adobe Acrobat Reader und geben Sie das vereinbarte Passwort ein.")}</p>`;
      // 0. Snapshot disabled — Sent Items copy not yet implemented.
      // const originalSnapshot = await captureEmailSnapshot();

      // 1. Collect mail data
      const [subject, bodyHtml, recipientsByCc, allItems] = await Promise.all([
        getSubject(),
        getBody(),
        getRecipientsWithCc(),
        getAttachmentItems(),
      ]);

      const attachmentItems = allItems.filter((a) => !a.isInline);

      const toRecipients = recipientsByCc.to
        .filter((r) => r.emailAddress)
        .map((r) => r.emailAddress);

      const ccRecipients = recipientsByCc.cc
        .filter((r) => r.emailAddress)
        .map((r) => r.emailAddress);

      // 2. Blocked extension check — per file, separate confirm
      for (const item of attachmentItems) {
        if (isBlockedAttachment(item.name)) {
          const ext = getFileExtension(item.name);
          const confirmed = window.confirm(
            t("Die Datei {name} kann vom Empfänger möglicherweise nicht geöffnet werden.\nDer Adobe Acrobat unterstützt das Öffnen von Dateien mit der Endung {ext} nicht.\nMöchten Sie fortfahren?", {
              name: item.name,
              ext,
            }),
          );
          if (!confirmed) {
            throw Object.assign(new Error("USER_ABORTED"), { code: "USER_ABORTED" });
          }
        }
      }

      // 3. Demo notice — window.confirm is not supported in Office Add-ins,
      //    so we show an inline notice via a custom error that PasswordModal
      //    can display without closing. For now, we silently continue and let
      //    the engine embed the demo watermark (isPaid: false).
      //    TODO: Replace with an inline banner in PasswordModal if needed.

      // 4. Fetch attachment content
      const attachments = [];
      for (const item of attachmentItems) {
        const { content } = await getAttachmentContent(item.id);
        attachments.push({
          fileName: item.name,
          contentType: "application/octet-stream",
          base64: content,
        });
      }

      // 5. EML (soft-fail — Office.js compose API does not expose raw EML)
      const includeEml = settings?.originalEmailEinbetten ?? false;
      const emlBase64 = includeEml ? await getEmlBase64() : null;

      // 6. Build meta
      const meta = {
        subject,
        sender: getUserEmail(),
        to: toRecipients.join(", "),
        cc: ccRecipients.join(", "),
        timestamp: new Date().toISOString(),
      };

      // 7. Strip HTML tags from body before passing to PDF engine
      const bodyPlainText = bodyHtml
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<\/li>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      // 8. Call engine
      console.log("[useSendOrchestrator] Calling PDF engine — isPaid:", !!isPaid, "attachments:", attachments.length);
      let result;
      try {
        result = await pdfEngine.buildContainerAndEncrypt({
          meta,
          bodyText: bodyPlainText,
          attachments,
          emlBase64,
          password,
          isPaid: !!isPaid,
        });
      } catch (engineErr) {
        console.error("[useSendOrchestrator] PDF engine error:", engineErr?.message, engineErr?.code, engineErr);
        throw engineErr;
      }
      // result.data = encrypted container PDF base64
      // result.errors = non-fatal issues (EML_EMBED_FAILED) — log and ignore
      if (result.errors?.length > 0) {
        console.warn("[useSendOrchestrator] Engine non-fatal issues:", result.errors);
      }

      // 9. Determine outgoing subject
      const finalSubject = keepSubjectUnencrypted
        ? subject
        : (settings?.vorlageBetreff ?? t("Verschlüsselte E-Mail"));

      // 10. Determine outgoing body + append logo
      // vorlageBodyHtml is included in BenutzerSettingsResponseDto and available via settings.
      const baseBody = settings?.vorlageBodyHtml ?? defaultBodyHtml;
      const logoDataUrl = await _fetchLogoBase64();
      const logoHtml = logoDataUrl
        ? `<br/><img src="${logoDataUrl}" alt="Secured by Avamboo Encrypt" style="width:200px;height:auto;display:block;margin-top:16px;" />`
        : "";
      const finalBody = baseBody + logoHtml;

      // 11. Remove all non-inline attachments
      for (const item of attachmentItems) {
        await removeAttachment(item.id);
      }

      // 12. Set new subject and body
      await setSubject(finalSubject);
      await setBody(finalBody);

      // 13. Attach encrypted container PDF
      await addBase64Attachment(result.data, t("Verschluesselte-Mail.pdf"), false);

      // 14. Save changes to compose item (subject, body, attachments are now encrypted).
      //     The compose window stays open — user sends manually via Outlook's Send button.
      await saveCompose();

      // 15. TODO: Sent Items copy disabled — will be re-enabled once properly implemented
      //     (save during prepare, delete on failure if send never happens).
      // return await _runPostSend("full", originalSnapshot);
      return { sendMode: "full", sentItemsFailed: false };
    },
    [pdfEngine, settings, isPaid, t], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Main entry point called by PasswordModal via onEncryptAndSend.
   * Throws on fatal errors (re-thrown to PasswordModal catch block).
   * Throws Error("USER_ABORTED") for intentional user cancellations
   * — PasswordModal must swallow USER_ABORTED silently.
   *
   * @param {{ mode: "full"|"pdfOnly", password: string, keepSubjectUnencrypted: boolean }} args
   */
  const handleEncryptAndSend = useCallback(
    async ({ mode, password, keepSubjectUnencrypted }) => {
      if (mode === "pdfOnly") {
        return await _encryptPdfAttachmentsOnly(password);
      } else {
        return await _buildAndSendContainer(password, keepSubjectUnencrypted);
      }
    },
    [_encryptPdfAttachmentsOnly, _buildAndSendContainer],
  );

  /**
   * Send the email as-is without any encryption.
   * Returns a post-send result for consistent handling in the caller.
   * @returns {Promise<{ sendMode: "unencrypted", sentItemsFailed: false }>}
   */
  const handleSendUnencrypted = useCallback(async () => {
    await saveCompose();
    return { sendMode: "unencrypted", sentItemsFailed: false };
  }, []);

  return { handleEncryptAndSend, handleSendUnencrypted };
}
